import { useState } from 'react'
import { renderToString } from 'react-dom/server'
import JSZip from 'jszip'
import { SensorType } from 'src/api'
import { useAddPopupMessage } from './useAddPopupMessage'
import SensorLabel from '../../components/icons/sensor-label'

export const useSensorLabel = () => {
  const [generatingLabel, setGeneratingLabel] = useState(false)
  const addMessage = useAddPopupMessage()

  const saveFile = (file: Blob, filename: string) => {
    const url = URL.createObjectURL(file)

    const downloadLink = document.createElement('a')
    downloadLink.href = url
    downloadLink.download = filename

    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(url)
  }

  const saveSvg = (svgData: string, filename: string) => {
    const preface = '<?xml version="1.0" standalone="no"?>\r\n'
    const svgBlob = new Blob([preface, svgData], {
      type: 'image/svg+xml;charset=utf-8'
    })
    saveFile(svgBlob, filename)
  }

  const downloadLabel = async (deviceId: string, type?: SensorType['name']) => {
    setGeneratingLabel(true)
    try {
      const svgData = renderToString(<SensorLabel deviceId={deviceId} type={type} />)
      saveSvg(svgData, `${deviceId}.svg`)
    } catch (err) {
      addMessage({ type: 'error', text: (err as Error).message })
    } finally {
      setGeneratingLabel(false)
    }
  }

  const downloadLabelsBulk = async (deviceIds: string[], fileName?: string) => {
    if (!deviceIds.length) {
      return
    }
    setGeneratingLabel(true)
    try {
      const zip = new JSZip()
      const fileNameWithoutExtension = fileName ? fileName.split('.').shift() : 'labels'
      await Promise.all(
        deviceIds.map(async (deviceId) => {
          const svgData = renderToString(<SensorLabel deviceId={deviceId} />)
          zip.file(`${fileNameWithoutExtension}/${deviceId}.svg`, `<?xml version="1.0" standalone="no"?>\r\n${svgData}`)
        })
      )
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const finalFileName = fileName ? `${fileNameWithoutExtension}.zip` : `sensor-labels-${Date.now()}.zip`
      saveFile(zipBlob, finalFileName)
    } catch (err) {
      addMessage({ type: 'error', text: (err as Error).message })
    } finally {
      setGeneratingLabel(false)
    }
  }

  return {
    generatingLabel,
    downloadLabel,
    downloadLabelsBulk
  }
}
